// utils/videoUtils.js
/**
 * 🎬 Hybrid Video Utilities
 * Production-ready utilities combining stability + scalability
 */

import { getServerUrl, getImageUrl } from '../config/apiConfig';

const SERVER_BASE_URL = getServerUrl();

/* =======================
   🎬 Video URL
======================= */
export const getVideoUrl = (lesson) => {
  if (!lesson) return '';

  // لو جاي String مباشر
  if (typeof lesson === 'string') return lesson;

  // 1️⃣ أولوية كاملة لـ video_url من DB
  const videoUrl = lesson.video_url || lesson.videoUrl;
  if (videoUrl) {
    // لو URL كامل
    if (videoUrl.startsWith('http')) return videoUrl;

    // لو مسار محلي
    return videoUrl.startsWith('/')
      ? `${SERVER_BASE_URL}${videoUrl}`
      : `${SERVER_BASE_URL}/${videoUrl}`;
  }

  // 2️⃣ Bunny Stream fallback - SECURITY: لا تقم ببناء رابط مباشر!
  if (lesson.bunny_video_id) {
    return null; // سيتم التعامل معه في Backend
  }

  return '';
};

/* =======================
   �️ Thumbnail
======================= */
export const getVideoThumbnail = (videoUrl, lesson = {}) => {
  // 1️⃣ Custom uploaded thumbnail (highest priority)
  if (lesson.thumbnail_url) {
    const thumbnailUrl = getImageUrl(lesson.thumbnail_url);
    return thumbnailUrl;
  }

  // 2️⃣ Bunny video thumbnail
  if (lesson.bunny_video_id) {
    const bunnyStreamHostname = process.env.REACT_APP_BUNNY_STREAM_HOSTNAME || 'vz-785ca6bf-af3.b-cdn.net';
    const thumb = `https://${bunnyStreamHostname}/${lesson.bunny_video_id}/thumbnail.jpg`;
    return thumb;
  }

  // 3️⃣ Bunny iframe thumbnail
  if (videoUrl?.includes('iframe.mediadelivery.net')) {
    const id = videoUrl.split('/').pop();
    const bunnyStreamHostname = process.env.REACT_APP_BUNNY_STREAM_HOSTNAME || 'vz-785ca6bf-af3.b-cdn.net';
    const thumb = `https://${bunnyStreamHostname}/${id}/thumbnail.jpg`;
    return thumb;
  }

  // 4️⃣ YouTube thumbnail
  const yt = extractYouTubeId(videoUrl);
  if (yt) {
    const thumb = `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
    return thumb;
  }

  // 5️⃣ Vimeo thumbnail
  const vimeoId = extractVimeoId(videoUrl);
  if (vimeoId) {
    const thumb = `https://vumbnail.com/${vimeoId}.jpg`;
    return thumb;
  }

  // 6️⃣ Bunny Stream direct videos (extract from video URL)
  if (videoUrl && videoUrl.includes('b-cdn.net')) {
    // Extract video ID from Bunny CDN URL
    const bunnyStreamHostname = process.env.REACT_APP_BUNNY_STREAM_HOSTNAME || 'vz-785ca6bf-af3.b-cdn.net';
    const bunnyId = videoUrl.match(/https:\/\/([^/]+)\/([^/]+)/)?.[2];
    if (bunnyId) {
      const thumb = `https://${bunnyStreamHostname}/${bunnyId}/thumbnail.jpg`;
      return thumb;
    }
  }

  // 🎬 Default video placeholder image
  return '/default_video.png';
};

/* =======================
   ⏱️ Duration
======================= */
export const getLessonDuration = (lesson) => {
  if (!lesson) return 0;

  if (lesson.duration_seconds > 0) return lesson.duration_seconds;
  if (lesson.data_seconds > 0) return lesson.data_seconds;

  if (typeof lesson.duration === 'number') {
    return lesson.duration < 1000
      ? lesson.duration
      : Math.floor(lesson.duration / 1000);
  }

  if (typeof lesson.duration === 'string') {
    if (lesson.duration.includes(':')) {
      return parseTimeString(lesson.duration);
    }

    const min = lesson.duration.match(/(\d+)\s*دق/);
    if (min) return parseInt(min[1]) * 60;

    const sec = lesson.duration.match(/(\d+)\s*ث/);
    if (sec) return parseInt(sec[1]);
  }

  return 0;
};

export const formatDuration = (seconds) => {
  if (!seconds) return '--:--';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s
        .toString()
        .padStart(2, '0')}`
    : `${m.toString().padStart(2, '0')}:${s
        .toString()
        .padStart(2, '0')}`;
};

export const formatDurationDisplay = (seconds) => {
  if (!seconds) return '--:--';
  
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}س ${m}د`;
  } else if (m > 0) {
    return `${m}د ${s}ث`;
  } else {
    return `${s}ث`;
  }
};

/* =======================
   👁️ Views
======================= */
export const getLessonViews = (lesson) =>
  lesson?.actual_views ||
  lesson?.views ||
  lesson?.view_count ||
  lesson?.total_views ||
  lesson?.stats?.views ||
  0;

// Robust parser for analytics responses (handles numeric, string, or full response object)
export const parseAnalyticsViews = (stored) => {
  if (stored == null) return null;
  // If server returned full response object
  if (typeof stored === 'object') {
    const candidate = stored?.data?.stats?.views ?? stored?.data?.views ?? stored?.stats?.views ?? stored?.views ?? null;
    const num = parseInt(candidate, 10);
    return Number.isNaN(num) ? null : num;
  }
  // If it's a primitive (string/number)
  const n = parseInt(stored, 10);
  return Number.isNaN(n) ? null : n;
};

/* =======================
   🎥 Video Type
======================= */
export const getVideoType = (videoUrl) => {
  if (!videoUrl) return 'unknown';
  if (videoUrl.includes('mediadelivery.net')) return 'bunny';
  if (videoUrl.includes('youtube') || videoUrl.includes('youtu.be'))
    return 'youtube';
  if (videoUrl.includes('vimeo.com')) return 'vimeo';
  if (videoUrl.startsWith('http')) return 'external';
  return 'local';
};

export const isExternalVideo = (videoUrl) =>
  ['youtube', 'vimeo', 'bunny', 'external'].includes(
    getVideoType(videoUrl)
  );

/* =======================
   ▶️ Embed URL
======================= */
export const getVideoEmbedUrl = (videoUrl) => {
  const type = getVideoType(videoUrl);

  switch (type) {
    case 'youtube': {
      const id = extractYouTubeId(videoUrl);
      return id
        ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`
        : videoUrl;
    }
    case 'vimeo': {
      const id = extractVimeoId(videoUrl);
      return id
        ? `https://player.vimeo.com/video/${id}?title=0&byline=0`
        : videoUrl;
    }
    default:
      return videoUrl;
  }
};

/* =======================
   🔎 Helpers
======================= */
export const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|embed\/)([^&?#]+)/
  );
  return match ? match[1] : null;
};

export const extractVimeoId = (url) => {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

export const parseTimeString = (str) => {
  const parts = str.split(':').map(Number);
  if (parts.length === 3)
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2)
    return parts[0] * 60 + parts[1];
  return parts[0] || 0;
};

/* =======================
   📊 Stats
======================= */
export const getVideoStats = (lesson) => {
  const url = getVideoUrl(lesson);
  const duration = getLessonDuration(lesson);

  return {
    url,
    embedUrl: getVideoEmbedUrl(url),
    thumbnail: getVideoThumbnail(url, lesson),
    duration,
    formattedDuration: formatDuration(duration),
    views: getLessonViews(lesson),
    type: getVideoType(url),
    isExternal: isExternalVideo(url),
    hasThumbnail: !!lesson?.thumbnail_url
  };
};

/* =======================
   📋 Lesson Data
======================= */
export const getLessonData = (lesson) => {
  if (!lesson) {
    return {
      duration: 0,
      views: 0,
      contentType: 'درس',
      badgeColor: 'bg-blue-500/70'
    };
  }

  const duration = getLessonDuration(lesson);
  const views = getLessonViews(lesson);
  const videoType = getVideoType(lesson.video_url);
  
  // Determine content type
  const contentType = lesson.content_type || videoType || 'درس';
  
  // Determine badge color based on content type
  let badgeColor = 'bg-blue-500/70';
  if (contentType?.toLowerCase()?.includes('فيديو')) {
    badgeColor = 'bg-red-500/70';
  } else if (contentType?.toLowerCase()?.includes('pdf')) {
    badgeColor = 'bg-orange-500/70';
  } else if (contentType?.toLowerCase()?.includes('اختبار')) {
    badgeColor = 'bg-green-500/70';
  } else if (lesson.is_quiz) {
    badgeColor = 'bg-green-500/70';
  }

  return {
    duration,
    views,
    contentType,
    badgeColor
  };
};

const videoUtils = {
  getVideoUrl,
  getVideoThumbnail,
  getLessonDuration,
  formatDuration,
  formatDurationDisplay,
  getLessonViews,
  getVideoType,
  isExternalVideo,
  getVideoEmbedUrl,
  extractYouTubeId,
  extractVimeoId,
  parseTimeString,
  getVideoStats,
  getLessonData
};

export default videoUtils;
